'use client';
import React from 'react';
import { AlertTriangle, TrendingDown, Clock, XCircle } from 'lucide-react';
import { Distributor } from '@/data/mockData';
import RankBadge from '@/components/ui/RankBadge';
import Icon from '@/components/ui/AppIcon';


interface Props {
  distributors: Distributor[];
}

export default function AlertsBottomPanel({ distributors }: Props) {
  const alerts = [
    ...distributors.filter(d => d.rank === 'Provisional Executive').map(d => ({
      id: `alert-pexec-${d.id}`,
      type: 'pexec' as const,
      name: d.name,
      rank: d.rank,
      message: `PEXEC — ${d.pexecMonthsRemaining} month(s) before lapse`,
      severity: 'high' as const,
    })),
    ...distributors.filter(d => d.active && d.psv < 100).map(d => ({
      id: `alert-psv-${d.id}`,
      type: 'psv' as const,
      name: d.name,
      rank: d.rank,
      message: `PSV ${d.psv} — below 100 minimum`,
      severity: 'high' as const,
    })),
    ...distributors.filter(d => d.qualStage === 'Q2').map(d => ({
      id: `alert-qual-${d.id}`,
      type: 'qual' as const,
      name: d.name,
      rank: d.rank,
      message: 'Q2 stage — final qualification month',
      severity: 'medium' as const,
    })),
    ...distributors.filter(d => d.graceMonthUsed && ['Executive', 'Emerald', 'Diamond', 'Blue Diamond', 'Presidential Director'].includes(d.rank)).map(d => ({
      id: `alert-grace-${d.id}`,
      type: 'grace' as const,
      name: d.name,
      rank: d.rank,
      message: 'Grace Month used — no buffer remaining',
      severity: 'medium' as const,
    })),
  ];

  const iconMap = {
    pexec: AlertTriangle,
    psv: TrendingDown,
    qual: Clock,
    grace: XCircle,
  };

  const colorMap = {
    high: 'text-red-400 bg-red-950/20 border-red-500/20',
    medium: 'text-amber-400 bg-amber-950/20 border-amber-500/20',
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-800 flex-shrink-0">
        <div className="flex items-center gap-2">
          <AlertTriangle size={13} className="text-red-400" />
          <span className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Active Alerts</span>
        </div>
        <span className="bg-red-600 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5">{alerts.length}</span>
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-thin divide-y divide-zinc-800/40">
        {alerts.map(alert => {
          const Icon = iconMap[alert.type];
          const colorClass = colorMap[alert.severity];
          return (
            <div key={alert.id} className={`flex items-center gap-2.5 px-3 py-2 border-l-2 ${alert.severity === 'high' ? 'border-l-red-500' : 'border-l-amber-500'} hover:bg-zinc-800/20 transition-colors`}>
              <Icon size={13} className={alert.severity === 'high' ? 'text-red-400' : 'text-amber-400'} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] font-semibold text-zinc-200 truncate">{alert.name}</span>
                  <RankBadge rank={alert.rank} size="xs" />
                </div>
                <p className="text-[10px] text-zinc-500 truncate">{alert.message}</p>
              </div>
            </div>
          );
        })}
        {alerts.length === 0 && (
          <div className="flex items-center justify-center h-full">
            <p className="text-xs text-zinc-600">No active alerts</p>
          </div>
        )}
      </div>
    </div>
  );
}